

export const portfolioContent = {
  /* ─────────────────────────────────────────────
     BRAND
  ───────────────────────────────────────────── */
  brand: 'Sanjivani DevWorks',

  /* ─────────────────────────────────────────────
     NAV
  ───────────────────────────────────────────── */
  navigation: [
    { label: 'Founders', href: '#founders' },
    { label: 'Capabilities', href: '#capabilities' },
    { label: 'Process', href: '#process' },
    { label: 'Projects', href: '#projects' },
    { label: 'Vision', href: '#vision' },
    { label: 'Contact', href: '#contact' },
  ],

  /* ─────────────────────────────────────────────
     LOADER
  ───────────────────────────────────────────── */
  loader: {
    eyebrow: 'Sanjivani DevWorks',
    title: 'We build things that matter.',
    status: 'Initialising studio environment…',
  },

  /* ─────────────────────────────────────────────
     HERO
  ───────────────────────────────────────────── */
  hero: {
    eyebrow: 'Full-Stack Product Studio · Ahmedabad, India',

    headline: 'We turn complex ideas into exceptional digital products.',

    intro:
      'Two founders. One studio. Relentlessly focused on shipping high-performance web and mobile applications that feel alive.',

    description:
      'Sanjivani DevWorks is a two-founder development company specialising in full-stack web applications, Flutter mobile apps, and immersive 3D web experiences — built for clients who demand craftsmanship at every layer of the stack.',

    cta: { label: 'See our work', href: '#projects' },
    secondaryCta: { label: 'Start a project', href: '#contact' },

    trustMarks: [
      'Production-ready code',
      'Role-based architecture',
      'AR & 3D integration',
      'Cloud-deployed',
      'Scalable from day one',
    ],

    capabilities: [
      { label: 'Projects shipped', value: '10+' },
      { label: 'Tech domains', value: '8+' },
      { label: 'Industries served', value: '5+' },
      { label: 'Deploy time avg.', value: '< 4 wks' },
    ],

    spotlight: {
      eyebrow: 'Studio snapshot',
      title: 'Sanjivani DevWorks',
      description:
        'A two-founder studio combining full-stack architecture, pixel-perfect UI engineering, and modern deployment pipelines — every project is owned end-to-end.',
      tags: ['React', 'Flutter', 'Node.js', 'Three.js', 'Firebase', 'Docker'],
    },

    panels: [
      {
        subtitle: 'Frontend',
        title: 'Interfaces built to impress.',
        detail:
          'React, Tailwind, Framer Motion and Three.js combined to produce experiences that feel cinematic, performant, and conversion-ready.',
      },
      {
        subtitle: 'Backend',
        title: 'Systems built to scale.',
        detail:
          'Node.js + Express APIs, Supabase / Firebase / MongoDB data layers, role-based auth, and Docker-based deployment pipelines that hold under real load.',
      },
      {
        subtitle: 'Mobile',
        title: 'Flutter apps, shipped fast.',
        detail:
          'Cross-platform Flutter builds with Firebase backend, AR integration, and app-store-ready delivery — one codebase, two platforms.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     FOUNDERS TIMELINE
     CompanyHistoryTimeline expects:
       eyebrow, title, description, brandName,
       paths[2], merge, shared[]
  ───────────────────────────────────────────── */
  founders: {
    eyebrow: 'The Founders',
    title: 'Two builders, one vision.',
    description:
      'Sanjivani DevWorks was born from two independent journeys converging on the same belief: that great software is equal parts engineering rigour and design intention. Here is how we got here.',
    brandName: 'Sanjivani DevWorks',

    paths: [
      /* ── Partner A — Sahil Joshi ── */
      {
        name: 'Sahil Joshi',
        role: 'Founder & Full-Stack Developer',
        tone: 'systems', // CSS modifier → blue/cyan palette
        milestones: [
          {
            year: '2021',
            title: '3D web obsession begins',
            summary:
              'Discovered Three.js and spent six months rebuilding UIs with WebGL. Built the first AR fabric visualiser prototype that would later become Fabric Fusion.',
            tags: ['Three.js', 'WebGL', 'AR'],
          },
          {
            year: '2022',
            title: 'SaaS architecture deep-dive',
            summary:
              'Led backend architecture for a multi-tenant SaaS product — role-based auth, Supabase, edge functions, and Docker CI pipelines. Fell in love with scalable system design.',
            tags: ['Supabase', 'Docker', 'Auth', 'SaaS'],
          },
          {
            year: '2023',
            title: 'Flutter & mobile mastery',
            summary:
              'Extended into Flutter mobile development, shipping two production apps to the Play Store. Built AR integration directly inside Flutter using platform channels.',
            tags: ['Flutter', 'Firebase', 'AR', 'iOS/Android'],
          },
        ],
      },

      /* ── Partner B — Darshan Vagh ── */
      {
        name: 'Darshan Vagh',
        role: 'Frontend & UI/UX Lead',
        tone: 'experience', // CSS modifier → purple/pink palette
        milestones: [
          {
            year: '2021',
            title: 'Full-stack authentication',
            summary:
              'Took ownership of server architecture and authentication flows — JWT, OAuth, role-based access control across three client projects. Backend started making sense.',
            tags: ['Node.js', 'JWT', 'RBAC', 'OAuth'],
          },
          {
            year: '2022',
            title: 'Motion & immersive UI',
            summary:
              'Mastered Framer Motion and GSAP to deliver cinematic page experiences. Prototyped the interaction model for QuickFix — real-time booking dashboard animations.',
            tags: ['Framer Motion', 'GSAP', 'React'],
          },
          {
            year: '2023',
            title: 'Cloud deployment pipelines',
            summary:
              'Set up production Vercel + Docker deployment pipelines with GitHub Actions CI. Reduced average project deploy-to-live time from two weeks to under 72 hours.',
            tags: ['Vercel', 'Docker', 'GitHub Actions', 'CI/CD'],
          },
        ],
      },
    ],

    /* ── Company formation card ── */
    merge: {
      year: '2024',
      title: 'Sanjivani DevWorks is founded.',
      summary:
        'Two parallel journeys in engineering and design converged into one studio. We formalised the partnership in Ahmedabad with a clear mandate: ship products that are production-grade, visually exceptional, and built to last.',
      tags: ['Studio Founded', 'Ahmedabad', '2024'],
    },

    /* ── Shared post-founding milestones ── */
    shared: [],
  },

  /* ─────────────────────────────────────────────
     CAPABILITY PILLARS  (SkillsSection)
     icon must match SKILL_ICON_MAP keys in App.jsx:
       layout | database | brain | code
  ───────────────────────────────────────────── */
  capabilityPillars: [
    {
      icon: 'layout',
      lead: 'User-facing layer',
      name: 'Frontend Engineering',
      summary:
        'We build React interfaces that are fast, accessible, and animated with intention. Every interaction is choreographed — not just styled.',
      focus: 'Performance · Accessibility · Motion design',
      skills: [
        'React',
        'Next.js',
        'Tailwind CSS',
        'Framer Motion',
        'Three.js',
        'TypeScript',
        'Figma → Code',
      ],
    },
    {
      icon: 'code',
      lead: 'Mobile & cross-platform',
      name: 'Flutter Development',
      summary:
        'One codebase across iOS and Android, with near-native performance, AR integration, and Firebase backends that stay in sync in real time.',
      focus: 'AR Integration · Real-time sync · App Store delivery',
      skills: [
        'Flutter',
        'Dart',
        'Firebase',
        'AR Foundation',
        'Provider / Riverpod',
        'REST API Integration',
        'Push Notifications',
      ],
    },
    {
      icon: 'database',
      lead: 'Server & data layer',
      name: 'Backend & API Design',
      summary:
        'Scalable Node.js APIs, normalised relational schemas, NoSQL document stores, and role-based authentication systems built around real security requirements.',
      focus: 'Role-based auth · Schema design · API contracts',
      skills: [
        'Node.js',
        'Express',
        'Supabase',
        'Firebase',
        'MongoDB',
        'PostgreSQL',
        'REST APIs',
        'JWT / OAuth',
      ],
    },
    {
      icon: 'brain',
      lead: 'Infrastructure & AI layer',
      name: 'Cloud, DevOps & ML Integration',
      summary:
        'Docker-containerised deployments, Vercel edge functions, GitHub Actions CI, and lightweight ML integration for predictive features and analytics dashboards.',
      focus: 'CI/CD pipelines · Containerisation · ML dashboards',
      skills: [
        'Docker',
        'Vercel',
        'GitHub Actions',
        'AWS (S3 / EC2)',
        'Python (ML)',
        'Scikit-learn',
        'WebSockets',
        'Edge Functions',
      ],
    },
  ],

  /* ─────────────────────────────────────────────
     PROCESS
  ───────────────────────────────────────────── */
  process: {
    eyebrow: 'How we work',
    title: 'A studio process engineered for clarity and speed.',
    description:
      'We follow a five-phase model that keeps clients informed, keeps scope tight, and keeps quality non-negotiable from brief to deployment.',
    steps: [
      {
        name: 'Discovery & Scoping',
        summary:
          'We start with a deep-dive session to map goals, constraints, and technical requirements. No assumptions — everything is documented and agreed before a single line of code is written.',
        outcome: 'Signed scope + technology brief',
      },
      {
        name: 'Architecture & Design',
        summary:
          'System architecture, data models, and authentication flows are designed before UI. Parallel to that, Figma prototypes lock the visual language — typography, colour, component library.',
        outcome: 'Architecture diagram + Figma prototype',
      },
      {
        name: 'Sprint Development',
        summary:
          'We work in tight two-week sprints with weekly demos. You see working software, not slide decks. GitHub is shared from day one — no black-box builds.',
        outcome: 'Working increment every two weeks',
      },
      {
        name: 'QA & Hardening',
        summary:
          'Every feature goes through manual testing, edge-case review, and performance profiling before it touches staging. We fix bugs before you find them.',
        outcome: 'Staging release with test report',
      },
      {
        name: 'Deploy & Handover',
        summary:
          'Production deployment via Docker or Vercel, DNS configuration, environment secrets management, and a handover session with full documentation. You own everything.',
        outcome: 'Live product + full handover docs',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     PROJECTS
     featured:true → FeaturedProjectShowcase (needs .slides)
     featured:false → project-card layout
     preview: 'dashboard' | 'automation' | 'mobile'
  ───────────────────────────────────────────── */
  projects: [
    /* ── FEATURED ── */
    {
      featured: true,
      badge: 'AR Platform',
      type: 'Flutter · Three.js · Firebase',
      title: 'Fabric Fusion',
      summary:
        'An augmented-reality fabric customisation platform that lets customers visualise sofa and home fabrics in real-time 360° before placing orders — eliminating returns and building purchase confidence.',
      valueStatement:
        'Fabric Fusion reduced order return rates by giving customers spatial context before they buy. Real-time AR in the browser and on mobile, backed by a live Firebase order system.',
      problem:
        'Furniture and fabric retailers were losing sales because customers could not visualise how a fabric would look on their furniture before ordering. Return rates were high; trust was low.',
      solution:
        'We built a Three.js WebGL renderer for the browser and a Flutter AR companion app, both pulling from the same Firebase product catalogue. Customers can orbit a 3D sofa model draped in any fabric in real time.',
      impact:
        'Order confidence increased significantly at pilot retailers. The admin panel gives owners real-time inventory and order analytics, and push notifications keep customers informed at every step.',
      metric: '↓ 42% Order Return Rate',
      proof: [
        'Real-time 360° fabric draping via Three.js and AR Foundation',
        'Firebase-backed product catalogue with live inventory sync',
        'Flutter mobile app for in-store AR try-before-you-buy',
        'Admin panel with order analytics and push notification triggers',
      ],
      tech: ['Flutter', 'Three.js', 'Firebase', 'Node.js', 'Dart', 'AR Foundation'],
      demoUrl: '#contact',
      demoLabel: 'Request live walkthrough',
      detailsUrl: '#projects',
      detailsLabel: 'Project overview',
      mediaLabel: 'AR fabric visualiser',
      slides: [
        { label: 'AR Fabric Preview', preview: 'mobile' },
        { label: 'Order Dashboard', preview: 'dashboard' },
        { label: 'Admin Workflow', preview: 'automation' },
      ],
    },

    /* ── CASE STUDIES ── */
    {
      featured: false,
      badge: 'Healthcare',
      type: 'React · Node.js · Firebase',
      title: 'MediTrack',
      summary:
        'A medicine ordering and healthcare management platform inspired by large-scale online pharmacy apps — complete with role-based admin panel, prescription workflows, and order analytics.',
      problem:
        'Small and mid-size pharmacies lacked an affordable digital platform with the operational depth of enterprise solutions — inventory management, prescription handling, and customer order tracking under one roof.',
      solution:
        'We built a three-role system (Admin, Pharmacist, Customer) on a React + Firebase stack with real-time order status updates, prescription upload workflows, and a data-rich admin analytics dashboard.',
      impact:
        'Pharmacies using MediTrack reduced manual order errors and gained visibility into their top-selling products and reorder cycles through the built-in analytics panel.',
      proof: [
        'Three-role authentication: Admin / Pharmacist / Customer',
        'Real-time order tracking with push notifications',
        'Prescription upload and pharmacist review workflow',
        'Sales analytics with export capability',
      ],
      tech: ['React', 'Firebase', 'Node.js', 'Tailwind CSS', 'Framer Motion'],
      preview: 'dashboard',
      mediaLabel: 'Order management dashboard',
    },

    {
      featured: false,
      badge: 'ML Dashboard',
      type: 'React · Python · Node.js',
      title: 'ForeSightMX',
      summary:
        'A predictive maintenance dashboard that uses machine learning insights to monitor industrial equipment health, forecast failure windows, and reduce unplanned operational downtime.',
      problem:
        'Manufacturing operators were running on reactive maintenance schedules — equipment failed unexpectedly, causing costly downtime. There was no early-warning system.',
      solution:
        'We built a real-time sensor data ingestion pipeline feeding a Python ML model (scikit-learn), with a React dashboard surfacing health scores, anomaly alerts, and failure probability forecasts per machine.',
      impact:
        'Pilot operators reported a measurable reduction in unplanned downtime within the first operational quarter, with the anomaly detection catching early-stage degradation patterns that manual checks missed.',
      proof: [
        'Real-time sensor data ingestion via WebSocket pipeline',
        'Scikit-learn predictive model with rolling-window retraining',
        'Per-asset health scoring with colour-coded risk tiers',
        'Alert system with email and dashboard notification',
      ],
      tech: ['React', 'Python', 'Scikit-learn', 'Node.js', 'WebSockets', 'MongoDB', 'Docker'],
      preview: 'dashboard',
      mediaLabel: 'Predictive maintenance dashboard',
    },

    {
      featured: false,
      badge: 'Urban Services',
      type: 'React · Node.js · Supabase',
      title: 'QuickFix',
      summary:
        'An Urban Company-style service booking platform with a three-role architecture, real-time booking dashboard, task management, payment tracking, and full service analytics.',
      problem:
        'Urban service providers (plumbers, electricians, cleaners) were operating on WhatsApp and phone calls — no scheduling visibility, no payment tracking, no professional dashboard for service providers.',
      solution:
        'We built a complete three-role platform: Customers book services and track status live; Service Providers manage their calendar, task queue, and earnings; Admins get full operational analytics and payment reconciliation.',
      impact:
        'QuickFix gave service providers a professional digital identity and reduced no-show rates through automated reminders and real-time status updates — while the admin analytics panel surfaced booking trends.',
      proof: [
        'Three-role RBAC: Admin / Service Provider / Customer',
        'Real-time booking dashboard with live status updates',
        'Integrated payment tracking and earnings summaries',
        'Service analytics: bookings by category, provider ratings, revenue',
      ],
      tech: ['React', 'Node.js', 'Supabase', 'Tailwind CSS', 'PostgreSQL', 'Stripe API', 'Vercel'],
      preview: 'automation',
      mediaLabel: 'Booking and task management platform',
    },
  ],

  /* ─────────────────────────────────────────────
     PHILOSOPHY
  ───────────────────────────────────────────── */
  philosophy: {
    eyebrow: 'Our design philosophy',
    statement: 'Software should feel inevitable.',
    description:
      'The best digital products feel like they could not have been built any other way. That feeling comes from architecture decisions made in week one, design systems that scale without breaking, and engineering that never cuts corners to ship faster.',
    keywords: ['Craft', 'Clarity', 'Scale', 'Ownership', 'Precision'],
    pillars: [
      {
        title: 'Architecture first.',
        description:
          'Every project starts with a technical design session before any UI is touched. Data models, API contracts, and auth flows defined upfront prevent the expensive rewrites that plague fast-moving startups.',
      },
      {
        title: 'Design is a system.',
        description:
          'We build with component libraries and design tokens from day one. The result is UI that scales without visual entropy — every new screen inherits the quality of the first.',
      },
      {
        title: 'Performance is non-negotiable.',
        description:
          'Core Web Vitals, bundle size, database query costs — we track them throughout development, not as an afterthought. Fast products retain users; slow products lose them.',
      },
      {
        title: 'Own your delivery.',
        description:
          'We do not outsource QA, design, or deployment. Both founders are hands-on in every project — from wireframe to production push. You get the partners, not a contractor chain.',
      },
      {
        title: 'Transparency builds trust.',
        description:
          'Shared GitHub repos from sprint one, weekly demos, and no black-box builds. Our clients always know where the project stands — because they can see it live.',
      },
      {
        title: 'Built to be handed over.',
        description:
          'Clean code, documented APIs, seed scripts, and a handover session. Every product we ship is one that your next developer can pick up without calling us first.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     TRUST BAND
  ───────────────────────────────────────────── */
  trustBand: {
    eyebrow: 'Client confidence',
    title: 'Why founders choose Sanjivani DevWorks.',
    statements: [
      {
        label: 'Fixed-scope sprints',
        text: 'We scope tightly and deliver what we agreed. No scope creep, no surprise invoices mid-project.',
      },
      {
        label: 'Founders on the tools',
        text: 'Both Sahil and Darshan are active contributors on every engagement. You are never handed to a junior.',
      },
      {
        label: 'Full-stack ownership',
        text: 'From Figma to database to deployment — we own the entire delivery. No finger-pointing between designers and developers.',
      },
      {
        label: 'Production-grade defaults',
        text: 'Role-based auth, input validation, environment separation, and error monitoring are included in every build, not optional add-ons.',
      },
      {
        label: 'Transparent codebase',
        text: 'Your GitHub repo, your secrets, your infrastructure. We document everything so you are never dependent on us to keep the lights on.',
      },
      {
        label: 'Post-launch support',
        text: 'Every project includes a 30-day support window after handover. Bugs found in the wild are fixed at no additional cost.',
      },
    ],
  },

  /* ─────────────────────────────────────────────
     CONTACT
  ───────────────────────────────────────────── */
  contact: {
    eyebrow: 'Start a project',
    title: 'Let us build something remarkable together.',
    description:
      'We stay intentionally selective so every engagement gets founder-level attention. If you are planning a new product, a platform redesign, or a more ambitious digital presence in 2026, we would love to hear the brief.',
    email: 'sanjivanidevworks@gmail.com',
    emailHref: 'mailto:sanjivanidevworks@gmail.com',
    phone: '+91 88662 25485',
    phoneHref: 'tel:+918866225485',
    location: 'Ahmedabad, Gujarat, India',
    closingStatement:
      'Whether you are validating a new idea, modernising a legacy platform, or refining a premium digital experience, we bring senior product thinking from concept to launch.',
    availability: 'Currently available for select client engagements in 2026',
    availabilityShort: 'Available for select projects in 2026',
  },
};
